import { supabase } from "@/lib/supabase";

/**
 * Il secondo fattore (TOTP), le due chiamate che servono a due pagine: l'attivazione
 * obbligatoria del titolare (`TotpEnrollmentPage`) e la pagina Sicurezza di ognuno.
 * Qui solo le chiamate a Supabase: quando chiamarle lo decide la pagina.
 */

// In supabase-js `fdata.totp` contiene SOLO i fattori verificati: quelli iniziati e
// mai confermati stanno in `fdata.all`. Serializzato, uno alla volta: nessun
// Promise.all.
async function togliNonConfermati() {
  const { data: fdata } = await supabase.auth.mfa.listFactors();
  const pending = (fdata?.all || []).filter((f) => f.factor_type === "totp" && f.status !== "verified");
  for (const f of pending) {
    try {
      await supabase.auth.mfa.unenroll({ factorId: f.id });
    } catch (_) {}
  }
}

const nomeUnico = () => `Interni D'Autore · ${Math.random().toString(36).slice(2, 8)}`;

/* Il nome con cui la voce compare nell'app di autenticazione. Senza `issuer` Supabase scrive il dominio del
   sito («gestionale.internidautore.com»). Vale per i codici attivati da adesso: quelli già attivi restano com'erano. */
const ISSUER = "Interni D'Autore";
const enroll = () => supabase.auth.mfa.enroll({ factorType: "totp", issuer: ISSUER, friendlyName: nomeUnico() });

/**
 * Chiede un fattore nuovo: QR, chiave da scrivere a mano, id del fattore.
 * Restituisce `{ data, error }` come `supabase.auth.mfa.enroll`.
 *
 * `mfa.enroll()` scrive una riga in `auth.mfa_factors` nel momento in cui parte:
 * si chiama solo quando la persona ha davvero deciso di attivarlo.
 */
export async function iscriviTotp() {
  await togliNonConfermati();
  // friendly_name unico per tentativo: anche se due chiamate scappassero in
  // parallelo (StrictMode in sviluppo), non colliderebbero sull'indice unico.
  let { data, error } = await enroll();

  // Un solo nuovo tentativo, se torna comunque un conflitto o un errore di
  // collisione: ripulisce di nuovo e riprova con un altro nome.
  const conflitto =
    error &&
    (error.code === "mfa_factor_name_conflict" ||
      error.status === 500 ||
      /friendly name/i.test(error.message || "") ||
      /already exists/i.test(error.message || "") ||
      /unexpected/i.test(error.message || ""));
  if (conflitto) {
    await togliNonConfermati();
    ({ data, error } = await enroll());
  }
  return { data, error };
}

/**
 * Conferma il fattore col codice a sei cifre. Restituisce `{ error, codiceSbagliato }`:
 * un errore della challenge (rete, sessione) non è un codice sbagliato, e va detto com'è.
 */
export async function confermaTotp(factorId, codice) {
  const { data: ch, error: ce } = await supabase.auth.mfa.challenge({ factorId });
  if (ce) return { error: ce, codiceSbagliato: false };
  const { error } = await supabase.auth.mfa.verify({ factorId, challengeId: ch.id, code: codice });
  return { error, codiceSbagliato: Boolean(error) };
}

/** Lascia perdere un'attivazione iniziata e non confermata. */
export async function annullaTotp(factorId) {
  try {
    await supabase.auth.mfa.unenroll({ factorId });
  } catch (_) {}
}
